import { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

interface DashboardNavConfigPanelProps {
  onSaved?: () => void;
}

interface DashboardNavConfig {
  enabled: boolean;
  label: string;
}

export function DashboardNavConfigPanel({ onSaved }: DashboardNavConfigPanelProps) {
  const [enabled, setEnabled] = useState(false);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient<DashboardNavConfig>('/dashboard-nav-config')
      .then((config) => {
        setEnabled(config.enabled);
        setLabel(config.label);
      })
      .catch(() => {
        // Use defaults
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const updated = await apiClient<DashboardNavConfig>('/dashboard-nav-config', {
        method: 'PATCH',
        body: { enabled, label },
      });
      setEnabled(updated.enabled);
      setLabel(updated.label);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      onSaved?.();
    } catch {
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '1rem 1.25rem', fontSize: '0.85rem', color: '#666' }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem 1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
          Enable Dashboard Nav
        </label>
        <button
          onClick={() => setEnabled(!enabled)}
          style={{
            width: '48px',
            height: '26px',
            borderRadius: '13px',
            border: 'none',
            backgroundColor: enabled ? '#1a6b3c' : '#ccc',
            position: 'relative',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
          aria-pressed={enabled}
          aria-label="Toggle dashboard navigation"
        >
          <span
            style={{
              position: 'absolute',
              top: '3px',
              left: enabled ? '25px' : '3px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          />
        </button>
        <span style={{ fontSize: '0.8rem', color: '#666' }}>
          {enabled ? 'Visible in sidebar' : 'Hidden from sidebar'}
        </span>
      </div>

      <div>
        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#333', marginBottom: '4px' }}>
          Menu Label
        </label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: '6px',
            fontSize: '0.9rem',
            boxSizing: 'border-box',
          }}
          placeholder="KDU 2026 Yai"
        />
        <p style={{ fontSize: '0.75rem', color: '#999', margin: '4px 0 0 0' }}>
          This label appears as the expandable menu header in the sidebar navigation.
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 20px',
            backgroundColor: '#1a6b3c',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        {saved && <span style={{ fontSize: '0.8rem', color: '#2e7d32' }}>✓ Saved</span>}
        {error && <span style={{ fontSize: '0.8rem', color: '#d32f2f' }}>{error}</span>}
      </div>
    </div>
  );
}
